/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  useCreateYachtMutation,
  useUpdateYachtMutation,
  useGetAdminCandidatesQuery,
  useGetClubByIdQuery,
} from '@/services/YatchManagementService';
import { KeenIcon } from '@/components';
import CircularProgress from '@mui/material/CircularProgress';
import {
  IYachtData,
  IYachtTranslation,
} from '@/types/yacht-management.types';
import LogoUpload from './LogoUpload';
import MediaUpload from '../habor-management/MediaUpload';
import { IClubData } from '@/types/club-management.types';
import clsx from 'clsx';
import { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { useGetAvailableHarborsQuery } from '@/services/ClubRegistrationService';
import * as Yup from 'yup';
import {
  businessIdValidation,
  mediaValidation,
  postcodeValidation,
} from '@/utils/validations';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useGetRegionsQuery } from '@/services/RegionManagementService';
import { PaginatedApiResponse } from '@/types/api.types';
import { UserResponse } from '@/types/user-management.types';
import Select from 'react-select';
import { RegionOption } from '../habor-management/HarborForm';
import YatchPreviewModal from './YatchPreviewModal';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl, IntlShape } from 'react-intl';
import { useTranslateHarborContentMutation } from '@/services/HarborManagementService';
import countries from 'world-countries';
import { localizedCountryOptions } from '@/utils/countryName';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import {
  getDialCodeFromCountryShortCode,
  normalizeDialCode,
  normalizeNationalPhoneNumber,
  resolvePhoneCountryShortCode,
} from '@/utils/phone';

const getYachtValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    name: Yup.string().required(
      intl.formatMessage({ id: 'YACHT.VALIDATION.NAME_REQUIRED' })
    ),
    location: Yup.string().notRequired(),
    description: Yup.string().notRequired(),
    contact_email: Yup.string()
      .email(
        intl.formatMessage({
          id: 'YACHT.VALIDATION.CONTACT_EMAIL_INVALID',
        })
      )
      .required(
        intl.formatMessage({
          id: 'YACHT.VALIDATION.CONTACT_EMAIL_REQUIRED',
        })
      ),
    website: Yup.string()
      .url(intl.formatMessage({ id: 'YACHT.VALIDATION.URL_INVALID' }))
      .notRequired(),
    street_address: Yup.string().required(
      intl.formatMessage({ id: 'YACHT.VALIDATION.STREET_REQUIRED' })
    ),
    postal_code: postcodeValidation(true, 'country_short_code'),
    city: Yup.string().required(
      intl.formatMessage({ id: 'YACHT.VALIDATION.CITY_REQUIRED' })
    ),
    business_id: businessIdValidation(true, 'country_short_code'),
    yacht_club_admin_id: Yup.string().test(
      'admin-required',
      intl.formatMessage({ id: 'YACHT.VALIDATION.ADMIN_REQUIRED' }),
      function (value) {
        return this.parent.mode !== 'create' || !!value;
      }
    ),
    region_id: Yup.string().optional(),
    harborIds: Yup.array().optional(),
    media: Yup.array()
      .of(mediaValidation(false, 5, 10, 2, 1))
      .max(5, intl.formatMessage({ id: 'YACHT.VALIDATION.MEDIA_MAX_FILES' }))
      .optional(),
  });

type Admin =
  | IClubData
  | {
      id: string;
      name: string | null;
      email: string;
      profile_picture: string | null;
    };

type YatchModalProps = {
  onSubmit?: (data: IYachtData) => void;
  mode?: 'create' | 'edit' | 'view';
};

type YachtFormBase = Omit<Partial<IYachtData>, 'status' | 'membershipStatus'>;

export interface FormValues extends YachtFormBase {
  mode: 'create' | 'edit' | 'view';
  status?: number | '';
  membershipStatus?: number | '';
  country?: string;
  country_code?: string;
  country_short_code?: string;
  contact_person_phone_country_short_code?: string;
  media_captions?: any[];
}

type YachtInitialData = Partial<IYachtData> & {
  contact_email?: string;
  harbors?: Array<{
    id: string;
    name: string;
    location: string;
  }>;
  country?: string;
  country_code?: string;
  country_short_code?: string;
  contact_person_phone_country_short_code?: string;
};

const hasTranslations = (
  value?: Record<string, IYachtTranslation> | null
): value is Record<string, IYachtTranslation> =>
  Boolean(value && Object.keys(value).length > 0);

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;

    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as {
        message?: unknown;
        error?: unknown;
      };

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;

  return undefined;
};

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

import { useParams } from 'react-router-dom';
import { Statuses, UserRoles } from '@/constant';

const YatchModal = ({ onSubmit, mode = 'view' }: YatchModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentLanguage, changeLanguage } = useLanguage();
  const intl = useIntl();
  const [initialData, setInitialData] = useState<any>({});

  const { id } = useParams();
  const { data: clubData } = useGetClubByIdQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (clubData) {
      const countryShortCode = clubData.country_short_code || '';
      let derivedCountryName = '';

      if (countryShortCode) {
        const countryMeta = countries.find((c) => c.cca2 === countryShortCode);
        if (countryMeta) {
          derivedCountryName = countryMeta.name?.common || '';
        }
      }

      const phoneCountryShortCode = resolvePhoneCountryShortCode(
        clubData.country_code,
        clubData.contact_person_phone_country_short_code,
        countryShortCode
      );
      const phoneCountryCode =
        normalizeDialCode(clubData.country_code) ||
        getDialCodeFromCountryShortCode(phoneCountryShortCode);

      const enhancedInitial: YachtInitialData = {
        // Map clubData into the yacht/club form shape
        ...(clubData as unknown as Partial<IYachtData>),
        contact_person_phone: clubData.contact_person_phone
          ? normalizeNationalPhoneNumber(
              clubData.contact_person_phone,
              phoneCountryShortCode
            )
          : '',
        yachtClubAdmin: clubData.yachtClubAdmin
          ? {
              id: clubData.yachtClubAdmin.id,
              name: clubData.yachtClubAdmin.name ?? '',
              email: clubData.yachtClubAdmin.email,
              profile_picture: null,
            }
          : undefined,
        country: derivedCountryName,
        country_code: phoneCountryCode,
        country_short_code: countryShortCode || undefined,
        contact_person_phone_country_short_code: phoneCountryShortCode || undefined,
        language: clubData.language || 'en',
        translations: clubData.translations || null,
        media_captions:
          (clubData as any).media_captions &&
          (clubData as any).media_captions.length > 0
            ? (clubData as any).media_captions
            : clubData.image_captions?.map((caption: any) => ({
                image_index: caption.image_index,
                captions: caption.translations,
              })) || [],
      };

      setInitialData(enhancedInitial);
    }
  }, [clubData]);

  const [createYacht, { isLoading: isCreating }] = useCreateYachtMutation({
    fixedCacheKey: 'shared-create-yacht',
  });
  const [updateYacht, { isLoading: isUpdating }] = useUpdateYachtMutation({
    fixedCacheKey: 'shared-update-yacht',
  });
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { data: adminResponse, isLoading: isAdminLoading } =
    useGetAdminCandidatesQuery(
      debouncedSearchTerm ? { search: debouncedSearchTerm } : {}
    );
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [mediaToDelete, setMediaToDelete] = useState<string[]>([]);
  const adminUsers: Admin[] =
    adminResponse?.data && Array.isArray(adminResponse.data)
      ? adminResponse.data.filter((user: Admin) => user.email)
      : [];

  const [isHarborModalOpen, setIsHarborModalOpen] = useState(false);
  const [harborIds, setHarborIds] = useState<string[]>(
    (initialData as YachtInitialData).harborIds || []
  );
  const userType = useSelector(
    (state: RootState) => state?.auth?.userData?.roles
  );

  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [translations, setTranslations] = useState<
    Record<string, IYachtTranslation>
  >({});
  const [previewSourceLanguage, setPreviewSourceLanguage] = useState<string>(
    currentLanguage.code || 'en'
  );
  const [formDataSnapshot, setFormDataSnapshot] = useState<
    Pick<Partial<IYachtData>, 'description'>
  >({});
  const [translationsValid, setTranslationsValid] = useState(false);
  const [translateHarborContent] = useTranslateHarborContentMutation();
  const previewInitialTranslations = useMemo(
    () =>
      hasTranslations(translations)
        ? translations
        : initialData.translations || {},
    [initialData.translations, translations]
  );
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const { data: regionsData, isLoading: isRegionsLoading } = useGetRegionsQuery(
    { status: Statuses.ACTIVE }
  ) as {
    data: PaginatedApiResponse<UserResponse[]> | undefined;
    isLoading: boolean;
    error: boolean;
  };

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4'];
  const countriesList = useMemo(
    () => localizedCountryOptions(currentLanguage.code || 'en'),
    [currentLanguage.code]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      name: (initialData as YachtInitialData).name || '',
      location: (initialData as YachtInitialData).location || '',
      description: (initialData as YachtInitialData).description || '',
      website: (initialData as YachtInitialData).website || '',
      contact_email: (initialData as YachtInitialData).contact_email || '',
      logo: (initialData as YachtInitialData).logo || undefined,
      abbreviation: (initialData as YachtInitialData).abbreviation || '',
      contact_person_name:
        (initialData as YachtInitialData).contact_person_name || '',
      contact_person_phone:
        (initialData as YachtInitialData).contact_person_phone || '',
      street_address: (initialData as YachtInitialData).street_address || '',
      postal_code: (initialData as YachtInitialData).postal_code || '',
      city: (initialData as YachtInitialData).city || '',
      business_id: (initialData as YachtInitialData).business_id || '',
      spv_id: (initialData as YachtInitialData).spv_id || '',
      region_id: (initialData as YachtInitialData).region_id || '',
      status: (initialData as YachtInitialData).status ?? '',
      membershipStatus:
        (initialData as YachtInitialData).membershipStatus ?? '',
      yacht_club_admin_id:
        (initialData as YachtInitialData).yacht_club_admin_id ||
        (initialData as YachtInitialData).admin_id ||
        (initialData as YachtInitialData).yachtClubAdmin?.id ||
        '',
      mode: mode,
      harborIds: (initialData as YachtInitialData).harborIds || [],
      media: (initialData as YachtInitialData).media || [],
      country: (initialData as YachtInitialData).country || '',
      country_code: (initialData as YachtInitialData).country_code || '',
      country_short_code:
        (initialData as YachtInitialData).country_short_code || '',
      contact_person_phone_country_short_code:
        (initialData as YachtInitialData).contact_person_phone_country_short_code ||
        (initialData as YachtInitialData).country_short_code ||
        '',
      media_captions: (initialData as YachtInitialData).media_captions || [],
    },
    validationSchema: getYachtValidationSchema(intl),
    onSubmit: async (values) => {
      try {
        let result;
        const isCreateMode = mode === 'create';

        const formData = new FormData();
        formData.append('name', values.name!.trim());
        formData.append('description', values.description!.trim());
        formData.append('location', values.location!.trim());
        formData.append('contact_email', values.contact_email!.trim());
        formData.append('yacht_club_admin_id', values.yacht_club_admin_id!);

        if (values.website) {
          formData.append('website', values.website.trim());
        }

        if (values.abbreviation)
          formData.append('abbreviation', values.abbreviation.trim());
        if (values.contact_person_name)
          formData.append(
            'contact_person_name',
            values.contact_person_name.trim()
          );
        if (values.contact_person_phone) {
          const raw = values.contact_person_phone.trim();
          const normalized = normalizeNationalPhoneNumber(
            raw,
            values.contact_person_phone_country_short_code || values.country_short_code
          );
          formData.append('contact_person_phone', normalized);
        }
        if (values.country_code) {
          formData.append('country_code', values.country_code.trim());
        }
        if (values.contact_person_phone_country_short_code) {
          formData.append(
            'contact_person_phone_country_short_code',
            values.contact_person_phone_country_short_code.trim()
          );
        }
        formData.append('street_address', (values.street_address ?? '').trim());
        formData.append('postal_code', (values.postal_code ?? '').trim());
        formData.append('city', (values.city ?? '').trim());
        // Only send the country short code to the API; no need to send country name or dial code
        if (values.country_short_code)
          formData.append(
            'country_short_code',
            values.country_short_code.trim()
          );
        if (values.business_id)
          formData.append('business_id', values.business_id.trim());
        if (values.spv_id) formData.append('spv_id', values.spv_id.trim());
        if (values.region_id)
          formData.append('region_id', values.region_id.trim());
        if (values.logo && values.logo instanceof File) {
          formData.append('logo', values.logo);
        } else if (
          !isCreateMode &&
          typeof initialData.logo === 'string' &&
          initialData.logo &&
          values.logo === null
        ) {
          formData.append('removeLogo', 'true');
        }

        if (harborIds.length > 0) {
          harborIds.forEach((id) => formData.append('harborIds[]', id));
        }

        if (values.media && Array.isArray(values.media)) {
          values.media.forEach((file) => {
            if (file instanceof File) {
              formData.append('media[]', file);
            }
          });
        }

        if (values.media_captions && values.media_captions.length > 0) {
          formData.append(
            'image_captions',
            JSON.stringify(values.media_captions)
          );
        }

        if (!isCreateMode && mediaToDelete.length > 0) {
          mediaToDelete.forEach((url) => {
            formData.append('mediaToDelete[]', url);
          });
        }

        // Ensure translations are included by default: generate if missing
        // Stale translations (description edited since they were generated)
        // must not be sent, or the API keeps returning the old text
        let translationsToUse = translationsValid ? translations : {};
        if (
          !hasTranslations(translationsToUse) &&
          values.description &&
          values.description.trim()
        ) {
          try {
            const translationResult = await translateHarborContent({
              description: values.description,
              source_language: currentLanguage.code || 'en',
            }).unwrap();
            const mapped: Record<string, { description?: string }> = {};
            if (translationResult && translationResult.translations) {
              Object.entries(translationResult.translations).forEach(
                ([langCode, translation]) => {
                  mapped[langCode] = {
                    description: (translation as { description?: string })
                      ?.description,
                  };
                }
              );
            }
            translationsToUse = mapped;
            setTranslations(mapped);
            setFormDataSnapshot({ description: values.description });
            setTranslationsValid(true);

            const warningMessage = translationResult?.warning_message;
            if (typeof warningMessage === 'string' && warningMessage.trim()) {
              enqueueSnackbar(warningMessage, { variant: 'warning' });
            } else if (Object.keys(mapped).length > 0) {
              enqueueSnackbar(translationResult?.message || 'Translations generated successfully', {
                variant: 'success',
              });
            }
          } catch (error) {
            enqueueSnackbar(
              getApiErrorMessage(error) ||
                intl.formatMessage({ id: 'YACHT.PREVIEW.TRANSLATION_ERROR' }),
              {
                variant: 'error',
              }
            );
          }
        }
        if (hasTranslations(translationsToUse)) {
          formData.append('translations', JSON.stringify(translationsToUse));
        }

        const mutationCall = isCreateMode
          ? (createYacht(formData) as unknown)
          : (updateYacht({
              yacht_id: (initialData as YachtInitialData).id || '',
              data: formData,
            }) as unknown);

        if (
          mutationCall &&
          typeof (mutationCall as any).unwrap === 'function'
        ) {
          result = await (mutationCall as any).unwrap();
        } else {
          result = await (mutationCall as Promise<any>);
        }

        if (handleSuccess) handleSuccess();

        if (onSubmit && result?.data) onSubmit(result.data);
        navigate('/club-management');

        formik.resetForm();
        setMediaToDelete([]);
      } catch (error: any) {
        console.error('Yacht submission error:', error);
        const message =
          error?.data?.message || error?.message || 'Something went wrong';
        enqueueSnackbar(message, { variant: 'error' });
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (mode === 'create' && !formik.values.country) {
      const finlandCountry = countriesList.find(
        (country) => country.code === 'FI'
      );
      if (finlandCountry) {
        const finlandData = countries.find(
          (c) => c.cca2 === finlandCountry.code
        );
        const countryCode = finlandData
          ? `${finlandData.idd.root || ''}${
              finlandData.idd.suffixes?.[0] || ''
            }`
          : '';

        formik.setValues((prev) => ({
          ...prev,
          country: finlandData?.name.common || 'Finland',
          country_code: countryCode,
          country_short_code: finlandCountry.code,
          contact_person_phone_country_short_code: finlandCountry.code,
        }));
      }
    }
  }, [mode, formik, formik.values.country, countriesList]);

  const { data: availableHarbors, isLoading: isHarborsLoading } =
    useGetAvailableHarborsQuery('', {
      skip: !isHarborModalOpen,
    });

  // Create combined harbors list for edit mode
  const getCombinedHarbors = () => {
    if (mode === 'create') {
      return availableHarbors?.harbors || [];
    }

    // For edit mode, combine existing harbors with available harbors
    const existingHarbors = (initialData as YachtInitialData).harbors || [];
    const availableHarborsList = availableHarbors?.harbors || [];

    // Create a map to avoid duplicates
    const harborMap = new Map();

    // Add existing harbors first
    existingHarbors.forEach((harbor) => {
      harborMap.set(harbor.id, harbor);
    });

    // Add available harbors, but don't overwrite existing ones
    availableHarborsList.forEach((harbor) => {
      if (!harborMap.has(harbor.id)) {
        harborMap.set(harbor.id, harbor);
      }
    });

    return Array.from(harborMap.values());
  };

  const combinedHarbors = getCombinedHarbors();

  const prevInitialData = useRef<string>();

  const handleSuccess = () => {
    enqueueSnackbar(
      mode === 'create'
        ? 'Yacht created successfully'
        : 'Yacht updated successfully',
      { variant: 'success' }
    );
  };

  useEffect(() => {
    const currentInitialData = JSON.stringify(initialData);

    if (currentInitialData !== prevInitialData.current) {
      formik.resetForm({
        values: {
          name: (initialData as YachtInitialData).name || '',
          location: (initialData as YachtInitialData).location || '',
          description: (initialData as YachtInitialData).description || '',
          website: (initialData as YachtInitialData).website || '',
          contact_email: (initialData as YachtInitialData).contact_email || '',
          logo: (initialData as YachtInitialData).logo || undefined,
          abbreviation: (initialData as YachtInitialData).abbreviation || '',
          contact_person_name:
            (initialData as YachtInitialData).contact_person_name || '',
          contact_person_phone:
            (initialData as YachtInitialData).contact_person_phone || '',
          street_address:
            (initialData as YachtInitialData).street_address || '',
          postal_code: (initialData as YachtInitialData).postal_code || '',
          city: (initialData as YachtInitialData).city || '',
          business_id: (initialData as YachtInitialData).business_id || '',
          spv_id: (initialData as YachtInitialData).spv_id || '',
          region_id: (initialData as YachtInitialData).region_id || '',
          status: (initialData as YachtInitialData).status ?? '',
          membershipStatus:
            (initialData as YachtInitialData).membershipStatus ?? '',
          yacht_club_admin_id:
            (initialData as YachtInitialData).yacht_club_admin_id ||
            (initialData as YachtInitialData).admin_id ||
            (initialData as YachtInitialData).yachtClubAdmin?.id ||
            '',
          mode: mode,
          harborIds: (initialData as YachtInitialData).harborIds || [],
          media: (initialData as YachtInitialData).media || [],
          country: (initialData as YachtInitialData).country || '',
          country_code: (initialData as YachtInitialData).country_code || '',
          country_short_code:
            (initialData as YachtInitialData).country_short_code || '',
          contact_person_phone_country_short_code:
            (initialData as YachtInitialData)
              .contact_person_phone_country_short_code ||
            (initialData as YachtInitialData).country_short_code ||
            '',
          media_captions:
            (initialData as YachtInitialData).media_captions || [],
        },
      });

      const existingHarborIds =
        (initialData as YachtInitialData).harbors?.map((h) => h.id) ||
        (initialData as YachtInitialData).harborIds ||
        [];
      setHarborIds(existingHarborIds);
      setMediaToDelete([]);
      const initialTranslations = (initialData as YachtInitialData)
        .translations;
      if (hasTranslations(initialTranslations)) {
        setTranslations(initialTranslations);
        setTranslationsValid(true);
        setFormDataSnapshot({
          description: (initialData as YachtInitialData).description,
        });
      } else {
        setTranslations({});
        setTranslationsValid(false);
        setFormDataSnapshot({});
      }
      prevInitialData.current = currentInitialData;
    }
  }, [initialData, mode, formik]);

  useEffect(() => {
    if (!translationsValid) return;

    if (formik.values.description !== formDataSnapshot.description) {
      setTranslationsValid(false);
    }
  }, [formik.values.description, formDataSnapshot, translationsValid]);

  useEffect(() => {
    if (regionsData?.data) {
      const options = regionsData.data.map((user: UserResponse) => ({
        value: user.id,
        label: user.name,
      }));
      setRegionOptions(options);
    }
  }, [regionsData]);

  const isSubmitting = formik.isSubmitting || isCreating || isUpdating;
  const { validateForm } = formik;
  const hasFormErrors = Object.keys(formik.errors).length > 0;

  useEffect(() => {
    if (hasFormErrors) {
      validateForm();
    }
  }, [currentLanguage.code, intl.locale, hasFormErrors, validateForm]);

  useEffect(() => {
    setPreviewSourceLanguage(currentLanguage.code || 'en');
  }, [currentLanguage.code]);

  const handleOpenPreview = () => {
    if (!formik.values.description || !formik.values.description.trim()) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'YACHT.VALIDATION.DESCRIPTION_REQUIRED_FOR_PREVIEW',
        }),
        {
          variant: 'warning',
        }
      );
      return;
    }
    setIsPreviewModalOpen(true);
  };

  const handleTranslationsGenerated = (
    newTranslations: Record<string, IYachtTranslation>
  ) => {
    setTranslations(newTranslations);
    setFormDataSnapshot({ description: formik.values.description });
    setTranslationsValid(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAdminDropdownOpen(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autofocus Name field on open (create/edit modes)
  useEffect(() => {
    if (mode !== 'view') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [mode]);

  const handleAdminDropdownToggle = () => {
    if (mode !== 'view' && !formik.isSubmitting && !isAdminLoading) {
      setIsAdminDropdownOpen(!isAdminDropdownOpen);
    }
  };

  const handleHarborSelection = (harborId: string, checked: boolean) => {
    setHarborIds((prev) =>
      checked ? [...prev, harborId] : prev.filter((x) => x !== harborId)
    );
  };

  const setFormData = (update: React.SetStateAction<Partial<FormValues>>) => {
    if (typeof update === 'function') {
      const nextValues = (
        update as (prev: Partial<FormValues>) => Partial<FormValues>
      )(formik.values);
      formik.setValues({ ...formik.values, ...nextValues });
    } else {
      formik.setValues({ ...formik.values, ...update });
    }
  };

  const setErrors = (
    update: React.SetStateAction<{ [key: string]: string }>
  ) => {
    if (typeof update === 'function') {
      const nextErrors = (
        update as (prev: { [key: string]: string }) => { [key: string]: string }
      )(formik.errors as any);
      formik.setErrors({ ...formik.errors, ...nextErrors });
    } else {
      formik.setErrors({ ...formik.errors, ...update });
    }
  };

  if (isAdminLoading) {
    return (
      <div className="p-4 text-center">
        <CircularProgress aria-label="Loading yacht details" />
        <p className="mt-2">Loading yacht details...</p>
      </div>
    );
  }

  return (
    <Fragment>
      <>
        <button
          type="button"
          onClick={() => navigate('/club-management')}
          data-testid="modal-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 focus:outline-none"
        >
          <KeenIcon type="filled" icon="close" className="h-6 w-6" />
        </button>
        <div className="container-fixed py-6 px-10 responsive-form yatch-modal">
          <div className="flex justify-between items-center mb-8 form-header">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {mode === 'create'
                  ? intl.formatMessage({ id: 'YACHT.FORM.CREATE.TITLE' })
                  : intl.formatMessage({ id: 'YACHT.FORM.EDIT.TITLE' })}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {mode === 'create'
                  ? intl.formatMessage({ id: 'YACHT.FORM.CREATE.DESCRIPTION' })
                  : intl.formatMessage({ id: 'YACHT.FORM.EDIT.DESCRIPTION' })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <select
                  id="yacht-language-select"
                  className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                  value={currentLanguage.code}
                  onChange={(e) => {
                    const selectedLang = I18N_LANGUAGES.find(
                      (lang: { code: string; label: string }) =>
                        lang.code === e.target.value
                    );
                    if (selectedLang) {
                      changeLanguage(selectedLang);
                      setPreviewSourceLanguage(selectedLang.code);
                    }
                  }}
                  aria-label={intl.formatMessage({
                    id: 'YACHT.FORM.SELECT_LANGUAGE',
                  })}
                >
                  {I18N_LANGUAGES.map(
                    (lang: { code: string; label: string }) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    )
                  )}
                </select>
                <img
                  src="/media/flags/globe.svg"
                  alt="Globe"
                  className="absolute dark:brightness-0 dark:invert contrast:brightness-0 contrast:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                  aria-hidden="true"
                />
              </div>
              <button
                type="button"
                onClick={handleOpenPreview}
                className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                aria-label={intl.formatMessage({ id: 'COMMON.SHOW_PREVIEW' })}
                disabled={isSubmitting}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
                {intl.formatMessage({ id: 'COMMON.SHOW_PREVIEW' })}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 header-cancel"
                onClick={() => navigate('/club-management')}
                aria-label={
                  mode === 'create'
                    ? intl.formatMessage({
                        id: 'YACHT.FORM.CANCEL.CREATE.ARIA',
                      })
                    : intl.formatMessage({ id: 'YACHT.FORM.CANCEL.EDIT.ARIA' })
                }
              >
                <KeenIcon
                  type="filled"
                  icon="cross"
                  className="text-gray-500"
                />
                {intl.formatMessage({ id: 'COMMON.CANCEL' })}
              </button>
            </div>
          </div>
          {mode === 'view' && clubData?.harbors?.length ? (
            <div className="mb-4">
              <h4 className="text-md font-medium text-gray-700 mb-2">
                Associated Harbors
              </h4>
              <div className="space-y-2">
                {clubData.harbors.map(
                  (harbor: { id: string; name: string; location: string }) => (
                    <div key={harbor.id} className="p-3 bg-gray-50 rounded">
                      <p className="font-medium">{harbor.name}</p>
                      <p className="text-sm text-gray-600">{harbor.location}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          ) : null}

          <form
            className="mt-4 space-y-4"
            aria-label={
              mode === 'create'
                ? 'Create yacht form'
                : mode === 'edit'
                  ? 'Edit yacht form'
                  : 'View yacht details'
            }
            onSubmit={formik.handleSubmit}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="form-control">
                <label htmlFor="name" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.NAME' })}
                  </span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_NAME_PLACEHOLDER',
                  })}
                  disabled={mode === 'view' || formik.isSubmitting}
                  ref={nameInputRef}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.name && formik.errors.name && 'input-error'
                  )}
                  autoComplete="name"
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.name}
                  </p>
                )}
              </div>

              <div className="form-control">
                {mode === 'view' ? (
                  <span className="label">
                    <span id="yacht-club-admin-label" className="label-text">
                      {intl.formatMessage({ id: 'YACHT.FORM.CLUB_ADMIN' })}
                    </span>
                  </span>
                ) : (
                  <label htmlFor="yacht_club_admin_id" className="label">
                    <span id="yacht-club-admin-label" className="label-text">
                      {intl.formatMessage({ id: 'YACHT.FORM.CLUB_ADMIN' })}
                    </span>
                  </label>
                )}
                <div
                  className="relative"
                  ref={adminDropdownRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className={clsx(
                      'input input-bordered w-full h-11 flex items-center justify-between ',
                      {
                        'cursor-pointer': !isSubmitting && mode !== 'view',
                        'cursor-not-allowed bg-gray-100 opacity-50':
                          isSubmitting || mode === 'view',
                        'border-primary':
                          isAdminDropdownOpen && mode !== 'view',
                        'border-red-500':
                          formik.touched.yacht_club_admin_id &&
                          formik.errors.yacht_club_admin_id,
                      }
                    )}
                    id="yacht_club_admin_id"
                    role="combobox"
                    aria-expanded={isAdminDropdownOpen}
                    aria-haspopup="listbox"
                    aria-controls="yacht-admin-options"
                    tabIndex={mode === 'view' ? -1 : 0}
                    aria-labelledby="yacht-club-admin-label"
                    onClick={
                      isSubmitting ||
                      mode === 'view' ||
                      userType?.includes(UserRoles.YACHT_CLUB_ADMIN)
                        ? undefined
                        : handleAdminDropdownToggle
                    }
                    onKeyDown={(e) => {
                      if (
                        isSubmitting ||
                        mode === 'view' ||
                        userType?.includes(UserRoles.YACHT_CLUB_ADMIN)
                      )
                        return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAdminDropdownToggle();
                      } else if (e.key === 'Escape' && isAdminDropdownOpen) {
                        setIsAdminDropdownOpen(false);
                      } else if (
                        e.key === 'ArrowDown' &&
                        !isAdminDropdownOpen
                      ) {
                        e.preventDefault();
                        setIsAdminDropdownOpen(true);
                      }
                    }}
                    aria-label={intl.formatMessage({
                      id: 'YACHT.FORM.SELECT_CLUB_ADMIN',
                    })}
                    aria-invalid={
                      formik.errors.yacht_club_admin_id ? 'true' : undefined
                    }
                    aria-describedby={
                      formik.errors.yacht_club_admin_id
                        ? 'admin-error'
                        : undefined
                    }
                  >
                    <div className="truncate">
                      {(() => {
                        const selectedAdmin =
                          adminUsers.find(
                            (a) => a.id === formik.values.yacht_club_admin_id
                          ) || (initialData as YachtInitialData).yachtClubAdmin;
                        return selectedAdmin
                          ? 'email' in selectedAdmin
                            ? selectedAdmin.name ||
                              selectedAdmin.email ||
                              intl.formatMessage({
                                id: 'YACHT.FORM.UNKNOWN_CLUB_ADMIN',
                              })
                            : selectedAdmin.name ||
                              selectedAdmin.email ||
                              intl.formatMessage({
                                id: 'YACHT.FORM.UNKNOWN_CLUB_ADMIN',
                              })
                          : intl.formatMessage({
                              id: 'YACHT.FORM.SELECT_CLUB_ADMIN',
                            });
                      })()}
                    </div>
                    {mode !== 'view' && (
                      <div className="flex items-center">
                        <svg
                          className={clsx(
                            'w-4 h-4 text-gray-400 transition-transform duration-200',
                            isAdminDropdownOpen && 'transform rotate-180'
                          )}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {isAdminDropdownOpen && (
                    <div
                      className="absolute z-10 w-full mt-1 bg-gray-100 border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          placeholder={intl.formatMessage({
                            id: 'YACHT.FORM.SEARCH_ADMINS',
                          })}
                          className="w-full input p-2 border rounded"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsAdminDropdownOpen(true);
                          }}
                          autoComplete="off"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div
                        id="yacht-admin-options"
                        role="listbox"
                        className="overflow-y-auto max-h-48"
                      >
                        {adminUsers.length > 0 ? (
                          adminUsers.map((user, index) => (
                            <div
                              key={user.id}
                              role="option"
                              aria-selected={
                                formik.values.yacht_club_admin_id === user.id
                              }
                              className="px-4 py-2 cursor-pointer hover:bg-gray-200 focus:bg-gray-200 focus:outline-none"
                              onClick={() => {
                                formik.setValues({
                                  ...formik.values,
                                  yacht_club_admin_id: user.id,
                                  contact_email:
                                    user.email || formik.values.contact_email,
                                });
                                setIsAdminDropdownOpen(false);
                                setSearchTerm('');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  formik.setValues({
                                    ...formik.values,
                                    yacht_club_admin_id: user.id,
                                    contact_email:
                                      user.email || formik.values.contact_email,
                                  });
                                  setIsAdminDropdownOpen(false);
                                  setSearchTerm('');
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const nextIndex =
                                    (index + 1) % adminUsers.length;
                                  const nextElement = e.currentTarget
                                    .parentElement?.children[
                                    nextIndex + 1
                                  ] as HTMLElement; // +1 to skip search input container
                                  nextElement?.focus();
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const prevIndex =
                                    index === 0
                                      ? adminUsers.length - 1
                                      : index - 1;
                                  const prevElement = e.currentTarget
                                    .parentElement?.children[
                                    prevIndex + 1
                                  ] as HTMLElement; // +1 to skip search input container
                                  prevElement?.focus();
                                } else if (e.key === 'Escape') {
                                  setIsAdminDropdownOpen(false);
                                  setSearchTerm('');
                                  // Focus back to the combobox
                                  const combobox = e.currentTarget
                                    .closest('.relative')
                                    ?.querySelector(
                                      '[role="combobox"]'
                                    ) as HTMLElement;
                                  combobox?.focus();
                                }
                              }}
                              tabIndex={0}
                            >
                              {user.name
                                ? `${user.name}${user.email ? ` (${user.email})` : ''}`
                                : user.email}
                            </div>
                          ))
                        ) : (
                          <div
                            role="option"
                            aria-selected="false"
                            aria-disabled="true"
                            className="px-4 py-2 text-gray-500"
                          >
                            {searchTerm &&
                            Array.isArray(adminResponse?.data) &&
                            adminResponse.data.length === 0
                              ? 'Admin not found'
                              : 'No admins available'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {formik.touched.yacht_club_admin_id &&
                    formik.errors.yacht_club_admin_id && (
                      <p className="mt-1 text-sm text-red-600" id="admin-error">
                        {formik.errors.yacht_club_admin_id}
                      </p>
                    )}
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'HARBOR.FORM.LOGO' })}
                  </span>
                </label>
                <LogoUpload
                  value={formik.values.logo as string | File | null}
                  onChange={(file) => formik.setFieldValue('logo', file)}
                  error={
                    formik.touched.logo && formik.errors.logo
                      ? formik.errors.logo
                      : undefined
                  }
                  disabled={mode === 'view'}
                  data-testid="logo-upload"
                  inputTestId="logo-input"
                  inputId="logo"
                  aria-label={intl.formatMessage({ id: 'HARBOR.FORM.LOGO' })}
                />
              </div>

              <div className="form-control">
                <label htmlFor="location" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.LOCATION' })}
                  </span>
                </label>
                <input
                  id="location"
                  name="location"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_LOCATION_PLACEHOLDER',
                  })}
                  autoComplete="off"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.location}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.location &&
                      formik.errors.location &&
                      'input-error'
                  )}
                />
                {formik.touched.location && formik.errors.location && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.location}
                  </p>
                )}
              </div>

              <div className="form-control">
                <label htmlFor="abbreviation" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.ABBREVIATION' })}
                  </span>
                </label>
                <input
                  id="abbreviation"
                  name="abbreviation"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_ABBREVIATION_PLACEHOLDER',
                  })}
                  autoComplete="off"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.abbreviation || ''}
                  className={clsx('input input-bordered w-full')}
                />
              </div>

              <div className="form-control sm:col-span-2">
                <label htmlFor="description" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
                  </span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_DESCRIPTION_PLACEHOLDER',
                  })}
                  rows={3}
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                  className={clsx(
                    'textarea textarea-bordered w-full',
                    formik.touched.description &&
                      formik.errors.description &&
                      'textarea-error'
                  )}
                />
                {formik.touched.description && formik.errors.description && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.description}
                  </p>
                )}
              </div>

              <div className="form-control">
                <label htmlFor="website" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.WEBSITE' })}
                  </span>
                </label>
                <input
                  id="website"
                  name="website"
                  type="url"
                  placeholder={intl.formatMessage({
                    id: 'COMMON.ENTER_WEBSITE_URL',
                  })}
                  autoComplete="off"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.website}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.website &&
                      formik.errors.website &&
                      'input-error'
                  )}
                />
                {formik.touched.website && formik.errors.website && (
                  <p className="mt-1 text-sm text-red-600" id="website-error">
                    {formik.errors.website}
                  </p>
                )}
              </div>

              <div className="form-control">
                <label htmlFor="contact_email" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.CONTACT_EMAIL' })}
                  </span>
                </label>
                <input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  placeholder={intl.formatMessage({
                    id: 'COMMON.ENTER_CONTACT_EMAIL',
                  })}
                  autoComplete="email"
                  disabled={true}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.contact_email}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.contact_email &&
                      formik.errors.contact_email &&
                      'input-error'
                  )}
                />
                {formik.touched.contact_email &&
                  formik.errors.contact_email && (
                    <p className="mt-1 text-sm text-red-600">
                      {formik.errors.contact_email}
                    </p>
                  )}
              </div>

              <div className="form-control">
                <label htmlFor="contact_person_name" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.CONTACT_PERSON_NAME' })}
                  </span>
                </label>
                <input
                  id="contact_person_name"
                  name="contact_person_name"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_CONTACT_PERSON_NAME_PLACEHOLDER',
                  })}
                  autoComplete="name"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.contact_person_name || ''}
                  className={clsx('input input-bordered w-full')}
                />
              </div>

              <div className="form-control">
                <label htmlFor="contact_person_phone" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.CONTACT_PERSON_PHONE' })}
                  </span>
                </label>
                <div className="flex gap-2">
                  <div className="w-32 flex-shrink-0">
                    <CountryCodeSelect
                      selected={
                        formik.values.contact_person_phone_country_short_code ||
                        formik.values.country_short_code ||
                        'FI'
                      }
                      setFieldValue={(dial, countryShortCode) => {
                        formik.setFieldValue('country_code', dial);
                        formik.setFieldValue('contact_person_phone_country_short_code', countryShortCode);
                      }}
                      disabled={mode === 'view' || formik.isSubmitting}
                    />
                  </div>
                  <input
                    id="contact_person_phone"
                    name="contact_person_phone"
                    type="tel"
                    placeholder={intl.formatMessage({
                      id: 'YACHT.FORM.ENTER_CONTACT_PERSON_PHONE_PLACEHOLDER',
                    })}
                    autoComplete="tel"
                    disabled={mode === 'view' || formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.contact_person_phone || ''}
                    className={clsx('input input-bordered w-full')}
                  />
                </div>
              </div>

              <div className="form-control">
                <label htmlFor="street_address" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.STREET_ADDRESS' })}
                  </span>
                </label>
                <input
                  id="street_address"
                  name="street_address"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_STREET_ADDRESS_PLACEHOLDER',
                  })}
                  autoComplete="address-line1"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.street_address}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.street_address &&
                      formik.errors.street_address &&
                      'input-error'
                  )}
                />
                {formik.touched.street_address &&
                  formik.errors.street_address && (
                    <p className="mt-1 text-sm text-red-600">
                      {formik.errors.street_address as string}
                    </p>
                  )}
              </div>

              <div className="form-control">
                <label htmlFor="postal_code" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.POSTAL_CODE' })}
                  </span>
                </label>
                <input
                  id="postal_code"
                  name="postal_code"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_POSTAL_CODE_PLACEHOLDER',
                  })}
                  autoComplete="postal-code"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.postal_code}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.postal_code &&
                      formik.errors.postal_code &&
                      'input-error'
                  )}
                />

                {formik.touched.postal_code && formik.errors.postal_code && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.postal_code as string}
                  </p>
                )}
              </div>

              <div className="form-control">
                <label htmlFor="city" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.CITY' })}
                  </span>
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_CITY_PLACEHOLDER',
                  })}
                  autoComplete="address-level2"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.city || ''}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.city && formik.errors.city
                      ? 'input-error border-red-500'
                      : ''
                  )}
                />
                {formik.touched.city && formik.errors.city && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.city as string}
                  </p>
                )}
              </div>

              <div className="form-control">
                {mode === 'view' ? (
                  <span className="label">
                    <span id="country-label" className="label-text">
                      {intl.formatMessage({ id: 'COMMON.COUNTRY' })}
                    </span>
                  </span>
                ) : (
                  <label
                    htmlFor="country-input"
                    className="label"
                    id="country-label"
                  >
                    <span className="label-text">
                      {intl.formatMessage({ id: 'COMMON.COUNTRY' })}
                    </span>
                  </label>
                )}
                <div ref={countryDropdownRef} className="relative">
                  <div
                    className={clsx(
                      'input input-bordered w-full h-11 flex items-center justify-between cursor-pointer',
                      formik.touched.country &&
                        formik.errors.country &&
                        'input-error',
                      mode === 'view' && 'bg-gray-100 cursor-not-allowed'
                    )}
                    id="country-input"
                    role={mode === 'view' ? undefined : 'combobox'}
                    aria-expanded={isCountryDropdownOpen}
                    aria-haspopup="listbox"
                    aria-controls="country-options"
                    tabIndex={mode === 'view' ? -1 : 0}
                    aria-labelledby="country-label"
                    onClick={() =>
                      mode !== 'view' &&
                      !formik.isSubmitting &&
                      setIsCountryDropdownOpen(!isCountryDropdownOpen)
                    }
                  >
                    <span className="truncate">
                      {countriesList.find(
                        (c) => c.code === formik.values.country_short_code
                      )?.name ||
                        formik.values.country ||
                        intl.formatMessage({ id: 'COMMON.SELECT_COUNTRY' })}
                    </span>
                    {!formik.isSubmitting && (
                      <KeenIcon
                        icon={isCountryDropdownOpen ? 'up' : 'down'}
                        className="text-gray-400"
                      />
                    )}
                  </div>

                  {isCountryDropdownOpen && !formik.isSubmitting && (
                    <div className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-gray-200 bg-gray-200">
                        <div className="relative">
                          <KeenIcon
                            icon="magnifier"
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            placeholder="Search countries..."
                            value={countrySearchTerm}
                            onChange={(e) =>
                              setCountrySearchTerm(e.target.value)
                            }
                            autoComplete="off"
                            className="input input-sm w-full pl-10 border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      <div
                        id="country-options"
                        role="listbox"
                        className="overflow-y-auto max-h-48"
                        tabIndex={0}
                      >
                        {(() => {
                          const filtered = countriesList.filter(
                            (country) =>
                              country.name
                                .toLowerCase()
                                .includes(countrySearchTerm.toLowerCase()) ||
                              country.code
                                .toLowerCase()
                                .includes(countrySearchTerm.toLowerCase())
                          );

                          if (filtered.length === 0) {
                            return (
                              <div
                                role="option"
                                aria-selected="false"
                                aria-disabled="true"
                                className="px-4 py-2.5 text-gray-500 text-sm"
                              >
                                No countries found
                              </div>
                            );
                          }

                          return filtered.map((country) => (
                            <div
                              key={country.code}
                              role="option"
                              aria-selected={
                                formik.values.country_short_code === country.code
                              }
                              className="px-4 py-2.5 cursor-pointer hover:bg-gray-200 flex items-center gap-2"
                              onClick={() => {
                                const countryData = countries.find(
                                  (c) => c.cca2 === country.code
                                );
                                formik.setValues((prev) => {
                                  return {
                                    ...prev,
                                    country: countryData?.name.common || country.name,
                                    country_short_code: country.code,
                                  };
                                }, true);
                                setIsCountryDropdownOpen(false);
                                setCountrySearchTerm('');
                              }}
                            >
                              <span className="text-sm">{country.name}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                {formik.touched.country && formik.errors.country && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.country as string}
                  </p>
                )}
              </div>

              <div className="form-control">
                <label htmlFor="business_id" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'COMMON.BUSINESS_ID' })}
                  </span>
                </label>
                <input
                  id="business_id"
                  name="business_id"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_BUSINESS_ID_PLACEHOLDER',
                  })}
                  autoComplete="off"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.business_id || ''}
                  className={clsx(
                    'input input-bordered w-full',
                    formik.touched.business_id &&
                      formik.errors.business_id &&
                      'input-error'
                  )}
                />
                {formik.touched.business_id && formik.errors.business_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.business_id as string}
                  </p>
                )}
              </div>

              <div className="form-control">
                <label htmlFor="spv_id" className="label">
                  <span className="label-text">
                    {intl.formatMessage({ id: 'YACHT.FORM.SPV_ID' })}
                  </span>
                </label>
                <input
                  id="spv_id"
                  name="spv_id"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'YACHT.FORM.ENTER_SPV_ID_PLACEHOLDER',
                  })}
                  autoComplete="off"
                  disabled={mode === 'view' || formik.isSubmitting}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.spv_id || ''}
                  className={clsx('input input-bordered w-full')}
                />
              </div>

              <div className="form-control">
                {mode === 'view' ? (
                  <span className="label">
                    <span className="label-text">
                      {intl.formatMessage({ id: 'COMMON.REGION' })}
                    </span>
                  </span>
                ) : (
                  <label htmlFor="region_id" className="label">
                    <span className="label-text">
                      {intl.formatMessage({ id: 'COMMON.REGION' })}
                    </span>
                  </label>
                )}
                <Select
                  id="region_id"
                  inputId="region_id"
                  name="region_id"
                  options={regionOptions}
                  value={
                    regionOptions.find(
                      (option) => option.value === formik.values.region_id
                    ) || null
                  }
                  onChange={(selectedOption: RegionOption | null) => {
                    formik.setFieldValue(
                      'region_id',
                      selectedOption?.value || ''
                    );
                  }}
                  onBlur={formik.handleBlur}
                  isDisabled={
                    mode === 'view' || formik.isSubmitting || isRegionsLoading
                  }
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : undefined
                  }
                  menuPosition="fixed"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 44,
                      height: 44,
                      padding: 0,
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingTop: 4,
                      paddingBottom: 4,
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: 44,
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: 280,
                    }),
                  }}
                  className={clsx(
                    '!react-select-container',
                    formik.touched.region_id && formik.errors.region_id
                      ? 'select-error border-red-500'
                      : ''
                  )}
                  classNamePrefix="select"
                  placeholder={intl.formatMessage({
                    id: 'COMMON.SELECT_REGIONS',
                  })}
                  aria-label={intl.formatMessage({
                    id: 'COMMON.SELECT_REGIONS',
                  })}
                  isClearable
                />
                {formik.touched.region_id && formik.errors.region_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.region_id as string}
                  </p>
                )}
              </div>

              <div className="form-control col-span-2">
                <MediaUpload
                  field="media"
                  media={formik.values.media}
                  setFormData={setFormData}
                  errors={Object.fromEntries(
                    Object.entries(formik.errors).map(([key, value]) => [
                      key,
                      value as string,
                    ])
                  )}
                  setErrors={setErrors}
                  disabled={mode === 'view' || isSubmitting}
                  allowedTypes={allowedTypes}
                  maxFiles={5}
                  previewMode={true}
                  media_captions={formik.values.media_captions}
                  onMediaRemoved={(removedMedia) => {
                    setMediaToDelete((prev) => [
                      ...prev,
                      ...removedMedia.filter((item) => !prev.includes(item)),
                    ]);
                  }}
                />
              </div>
            </div>

            {mode !== 'view' && (
              <div className="form-control col-span-2 mt-4">
                <label className="label">
                  <span className="label-text">
                    Associated Harbors
                  </span>
                </label>
                <div>
                  <button
                    type="button"
                    data-testid="harbors-button"
                    className="btn btn-sm btn-light"
                    onClick={() => setIsHarborModalOpen(true)}
                  >
                    {harborIds.length > 0 
                      ? `Manage Harbors (${harborIds.length} selected)`
                      : 'Select Harbors'
                    }
                  </button>
                </div>
              </div>
            )}

            {mode !== 'view' && (
              <div className="mt-8 flex justify-end gap-3 footer-actions">
                <button
                  type="button"
                  onClick={() => navigate('/club-management')}
                  className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500 btn-cancel"
                  aria-label="Cancel"
                  disabled={formik.isSubmitting}
                >
                  {intl.formatMessage({ id: 'COMMON.CANCEL' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={clsx(
                    'btn btn-sm btn-primary rounded-lg px-4 py-2',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500',
                    {
                      'opacity-50 cursor-not-allowed': isSubmitting,
                    }
                  )}
                  aria-label={
                    mode === 'create' ? 'Create yacht' : 'Update yacht'
                  }
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                      aria-label="Submitting yacht form"
                    />
                  ) : mode === 'create' ? (
                    intl.formatMessage({ id: 'COMMON.CREATE' })
                  ) : (
                    intl.formatMessage({ id: 'COMMON.UPDATE' })
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
        {isHarborModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]">
            <div
              className="bg-gray-100 p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar rounded-lg"
              role="dialog"
              aria-modal="true"
              aria-labelledby="assign-harbors-title"
              aria-describedby="assign-harbors-description"
              tabIndex={-1}
            >
              <h2
                id="assign-harbors-title"
                className="text-lg font-medium text-gray-900 mb-4"
              >
                {mode === 'create' ? 'Add Harbors' : 'Edit Harbors'}
              </h2>
              {isHarborsLoading ? (
                <div className="py-8 text-center text-sm text-gray-600">
                  Loading harbors...
                </div>
              ) : combinedHarbors.length > 0 ? (
                <>
                  <div
                    id="assign-harbors-description"
                    className="text-sm text-gray-700 mb-4"
                  >
                    Select harbors to assign to this club:
                  </div>
                  <div className="max-h-64 overflow-auto border border-gray-200 rounded-md p-3 mb-6 custom-scrollbar">
                    <div className="space-y-2">
                      {combinedHarbors.map((h) => {
                        const isExistingHarbor =
                          mode === 'edit' &&
                          (initialData as YachtInitialData).harbors?.some(
                            (existing) => existing.id === h.id
                          );

                        return (
                          <label
                            key={h.id}
                            className={`flex items-center gap-3 py-2 px-1 cursor-pointer hover:bg-gray-200 rounded transition-colors ${
                              isExistingHarbor
                                ? 'bg-blue-50 border border-blue-200'
                                : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={harborIds.includes(h.id)}
                              onChange={(e) =>
                                handleHarborSelection(h.id, e.target.checked)
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                            />
                            <div className="flex-1">
                              <span className="text-sm text-gray-900 select-none cursor-pointer">
                                {h.name}
                              </span>
                              {isExistingHarbor && (
                                <span className="ml-2 text-xs text-blue-600 font-medium">
                                  (Previously selected)
                                </span>
                              )}
                              {h.location && (
                                <div className="text-xs text-gray-500">
                                  {h.location}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setIsHarborModalOpen(false)}
                      aria-label="Cancel harbor assignment"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => {
                        formik.setFieldValue('harborIds', harborIds);
                        formik.setFieldTouched('harborIds', true, false);
                        setIsHarborModalOpen(false);
                      }}
                      aria-label="Save harbor selection"
                    >
                      Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div
                    id="assign-harbors-description"
                    className="text-sm text-gray-700 mb-6 text-center py-4"
                  >
                    No harbors available.
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setIsHarborModalOpen(false)}
                      aria-label="Close modal"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {isPreviewModalOpen && (
          <YatchPreviewModal
            open={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            formData={{
              id: (initialData as YachtInitialData).id,
              name: formik.values.name,
              description: formik.values.description,
              location: formik.values.location,
              language: (initialData as YachtInitialData).language,
              translations: (initialData as YachtInitialData).translations,
            }}
            source_language={
              previewSourceLanguage ||
              (initialData as YachtInitialData).language ||
              currentLanguage.code ||
              intl.locale
            }
            initialTranslations={previewInitialTranslations}
            onTranslationsGenerated={handleTranslationsGenerated}
          />
        )}
      </>
    </Fragment>
  );
};

export { YatchModal };
